import { Helmet } from "react-helmet-async";
import { Briefcase, MapPin, ArrowRight, Mail, Users, HeartHandshake } from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/career`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;
const jobApplyEmail = "careers@sevaafoundation.com";

type JobItem = {
  title: string;
  location: string;
  type: string;
  description: string;
  keywords: string;
};

const seoKeywords = [
  "SEVAA Foundation Careers",
  "Sevaa Foundation Careers",
  "NGO Jobs India",
  "NGO Career Opportunities",
  "Non Profit Jobs India",
  "Social Work Jobs",
  "Charity Jobs India",
  "Field Coordinator NGO Job",
  "Fundraising Executive NGO",
  "Social Media Manager NGO",
  "Work with NGO",
  "Join NGO Team",
  "NGO Jobs in Delhi",
  "NGO Jobs in Mumbai",
  "Remote NGO Jobs",
  "Full Time NGO Jobs",
  "Part Time NGO Jobs",
  "Social Welfare Jobs",
  "Community Development Jobs",
  "Fundraising Jobs India",
  "CSR NGO Jobs",
  "Volunteer Management Jobs",
  "Noida NGO Jobs",
  "NGO in Jharkhand Jobs",
  "NGO in Chatra Jharkhand",
  "NGO in Pakur Jharkhand",
  "समाज सेवा नौकरी",
  "NGO में नौकरी",
  "सेवा फाउंडेशन करियर",
  "सामाजिक संस्था में नौकरी",
  "फील्ड कोऑर्डिनेटर नौकरी",
  "फंडरेजिंग एग्जीक्यूटिव",
  "सोशल मीडिया मैनेजर नौकरी",
  "चैरिटी जॉब्स",
  "समाज सेवा करियर",
];

const jobs: JobItem[] = [
  {
    title: "Field Coordinator",
    location: "New Delhi",
    type: "Full-Time",
    description:
      "Coordinate SEVAA Foundation field activities, community drives, volunteer operations and social welfare programs.",
    keywords:
      "Field Coordinator NGO Job, field work NGO, social work job, community development job, NGO jobs in Delhi",
  },
  {
    title: "Social Media Manager",
    location: "Remote",
    type: "Part-Time",
    description:
      "Manage SEVAA Foundation social media content, campaign communication, storytelling and digital awareness for NGO initiatives.",
    keywords:
      "Social Media Manager NGO, remote NGO job, nonprofit social media job, charity content manager, NGO digital marketing",
  },
  {
    title: "Fundraising Executive",
    location: "Mumbai",
    type: "Full-Time",
    description:
      "Support SEVAA Foundation fundraising, donor communication, CSR outreach and partnership development.",
    keywords:
      "Fundraising Executive NGO, fundraising jobs India, donor relations, CSR fundraising, NGO jobs in Mumbai",
  },
];

const careerPageSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Careers at SEVAA Foundation",
  alternateName: "NGO Jobs and Career Opportunities at SEVAA Foundation",
  url: pageUrl,
  inLanguage: ["en-IN", "hi-IN"],
  description:
    "Explore career opportunities at SEVAA Foundation. Apply for NGO jobs, social work roles, field coordinator positions, fundraising roles and nonprofit career opportunities.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: logoImage,
    areaServed: {
      "@type": "Country",
      name: "India",
    },
  },
  about: [
    "NGO Jobs",
    "Non Profit Careers",
    "Social Work Jobs",
    "Field Coordinator Jobs",
    "Fundraising Jobs",
    "Social Media NGO Jobs",
    "Community Development Jobs",
    "CSR Jobs",
    "Charity Jobs",
  ],
  hasPart: jobs.map((job) => ({
    "@type": "JobPosting",
    title: job.title,
    description: job.description,
    employmentType: job.type === "Full-Time" ? "FULL_TIME" : "PART_TIME",
    datePosted: "2026-06-08",
    validThrough: "2026-12-31T23:59:59+05:30",
    hiringOrganization: {
      "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
      sameAs: siteUrl,
      logo: logoImage,
    },
    jobLocation:
      job.location === "Remote"
        ? {
            "@type": "Place",
            address: {
              "@type": "PostalAddress",
              addressCountry: "IN",
            },
          }
        : {
            "@type": "Place",
            address: {
              "@type": "PostalAddress",
              addressLocality: job.location,
              addressCountry: "IN",
            },
          },
    applicantLocationRequirements: {
      "@type": "Country",
      name: "India",
    },
    directApply: true,
    url: pageUrl,
    keywords: job.keywords,
  })),
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
  alternateName: "Swadesh Sevaa Charitable Foundation",
  url: siteUrl,
  logo: logoImage,
  description:
    "SEVAA Foundation is a nonprofit organization working for education, healthcare, food security, women empowerment, elderly care, tree plantation and social welfare.",
  areaServed: {
    "@type": "Country",
    name: "India",
  },
  knowsAbout: [
    "Child Education",
    "Healthcare Support",
    "Food Security",
    "Women Empowerment",
    "Tree Plantation",
    "Old Age Support",
    "Volunteer Programs",
    "CSR Partnerships",
    "Social Welfare",
  ],
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: siteUrl,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Career",
      item: pageUrl,
    },
  ],
};

const Career = () => {
  return (
    <>
      <Helmet>
        <title>Careers at SEVAA Foundation | NGO Jobs & Social Work</title>

        <meta name="title" content="Careers at SEVAA Foundation | NGO Jobs & Social Work" />

        <meta name="description" content="Explore NGO career opportunities at SEVAA Foundation, including field coordination, fundraising and social media roles in nonprofit social work." />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />

        <meta
          property="og:title"
          content="Careers at SEVAA Foundation | NGO Jobs India"
        />

        <meta
          property="og:description"
          content="Join SEVAA Foundation and build a meaningful career in social work, NGO operations, fundraising, digital awareness and community development."
        />

        <meta property="og:image" content={defaultImage} />
        <meta
          property="og:image:alt"
          content="SEVAA Foundation Careers and NGO Jobs"
        />
                <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
<meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="SEVAA Foundation Careers | NGO Jobs & Social Work"
        />

        <meta
          name="twitter:description"
          content="Apply for NGO jobs and nonprofit career opportunities at SEVAA Foundation."
        />

        <meta name="twitter:image" content={defaultImage} />

                <meta name="twitter:image:alt" content="SEVAA Foundation Careers and NGO Jobs" />
<script type="application/ld+json">
          {JSON.stringify(careerPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen font-poppins">
        {/* HERO SECTION */}
        <section
          aria-labelledby="career-heading"
          className="relative bg-teal-800 text-white py-20 px-6 text-center overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none"
            aria-hidden="true"
          ></div>

          <div className="relative z-10 max-w-4xl mx-auto">
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-[#ffb000] text-xs font-black uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-5">
              <HeartHandshake size={14} />
              Join Our Mission
            </span>

            <h1
              id="career-heading"
              className="text-4xl md:text-6xl font-black mb-4 uppercase leading-tight"
            >
              Work With Us
            </h1>

            <p className="text-teal-100 text-lg max-w-2xl mx-auto leading-relaxed">
              Build a career that makes a difference. Join SEVAA Foundation’s
              team of passionate changemakers working for education, healthcare,
              food security, women empowerment, elderly care, tree plantation
              and social welfare.
            </p>
          </div>
        </section>

        {/* JOBS SECTION */}
        <section
          aria-labelledby="current-openings-heading"
          className="max-w-4xl mx-auto py-16 px-6"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <span className="text-orange-600 font-black text-xs uppercase tracking-[0.2em]">
                Current Openings
              </span>

              <h2
                id="current-openings-heading"
                className="text-2xl md:text-3xl font-black text-slate-900 mt-2"
              >
                NGO Career Opportunities
              </h2>
            </div>

            <div className="flex items-center gap-2 text-slate-500 text-sm font-semibold">
              <Users size={18} className="text-teal-700" />
              Social Impact Roles
            </div>
          </div>

          <div className="space-y-4">
            {jobs.map((job, index) => (
              <article
                key={job.title}
                className="bg-white p-6 rounded-2xl border border-slate-200 flex flex-col md:flex-row md:items-center justify-between group hover:border-orange-500 transition-colors shadow-sm"
              >
                <div>
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-orange-600 transition-colors">
                    {job.title}
                  </h3>

                  <p className="text-slate-500 text-sm font-medium mt-2 max-w-xl leading-relaxed">
                    {job.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-4 mt-3 text-slate-500 text-sm">
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      {job.location}
                    </span>

                    <span className="flex items-center gap-1">
                      <Briefcase size={14} />
                      {job.type}
                    </span>
                  </div>
                </div>

                <a
                  href={`mailto:${jobApplyEmail}?subject=${encodeURIComponent(
                    `Application for ${job.title}`
                  )}`}
                  className="mt-5 md:mt-0 inline-flex items-center gap-2 text-orange-600 font-bold hover:gap-3 transition-all"
                  aria-label={`Apply for ${job.title} at SEVAA Foundation`}
                >
                  Apply Now <ArrowRight size={18} />
                </a>
              </article>
            ))}
          </div>

          <div className="mt-12 p-8 bg-orange-50 rounded-2xl border border-orange-100 text-center">
            <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center mx-auto mb-4 shadow-sm">
              <Mail className="text-orange-600" size={24} />
            </div>

            <p className="text-slate-700 leading-relaxed">
              Don&apos;t see a role that fits? Send your resume to{" "}
              <a
                href={`mailto:${jobApplyEmail}`}
                className="font-bold text-orange-600 hover:text-orange-700"
              >
                {jobApplyEmail}
              </a>{" "}
              and we&apos;ll keep you in mind for upcoming NGO jobs, social work
              roles and nonprofit opportunities.
            </p>
          </div>
        </section>
      </main>
    </>
  );
};

export default Career;